import { IComboBoxOption } from "@fluentui/react";
import { ExperimentType, PipelineStatus, PipelineStatusMap } from "../Types";
import {
  FilterTypes,
  onComboFilterChange,
} from "../Shared/FilterComponent/FilterComponent";
import { DateOperator } from "../Shared/FilterComponent/DateRangeFilter";
import {
  MPAC_TYPE,
  PROD_TYPE,
  RC_TYPE,
} from "../Experiment/RolloutDetails/RolloutDetailsTable/RolloutDetailsEv2Panel/ev2TreatmentStages.consts";
import { normalizeStatus } from "../Shared/shared.utils";
import {
  DurationIncrement,
  DurationOption,
  Operator,
} from "../Shared/FilterComponent/DurationFilter";

const getFilters = (
  items: ExperimentType[],
  appliedFilters: { [key: string]: string[] },
  setFilters: React.Dispatch<React.SetStateAction<{ [key: string]: any[] }>>,
) => {
  return [
    {
      key: "lastModifiedBy",
      text: "Last Modified By",
      type: FilterTypes.Combobox,
      clearable: true,
      searchable: true,
      selectedKey: appliedFilters["lastModifiedBy"] || [],
      options: Array.from(
        new Set(
          items
            .filter(({ lastModifiedBy }) => lastModifiedBy)
            .map(({ lastModifiedBy }) => lastModifiedBy as string),
        ),
      )
        .sort()
        .map((lastModifiedBy) => ({
          key: lastModifiedBy,
          text: lastModifiedBy,
        })),
      onChange: (_: any, option: IComboBoxOption | undefined) =>
        onComboFilterChange("lastModifiedBy", option, setFilters),
    },
    {
      key: "status",
      text: "Status",
      type: FilterTypes.Combobox,
      clearable: true,
      selectedKey: appliedFilters["status"] || [],
      options: [
        {
          key: PipelineStatus.COMPLETED,
          text: PipelineStatus.COMPLETED,
        },
        {
          key: PipelineStatus.RUNNING,
          text: PipelineStatus.RUNNING,
        },
        { key: PipelineStatus.FAILED, text: PipelineStatus.FAILED },
      ],
      onChange: (_: any, option: IComboBoxOption | undefined) =>
        onComboFilterChange("status", option, setFilters),
    },
    {
      key: "lastModifiedDate",
      text: "Last Modified Date",
      type: FilterTypes.DateTimeOperator,
      clearable: true,
      selectedDate: (() => {
        try {
          const filterStr = appliedFilters["lastModifiedDate"]?.[0];
          if (!filterStr) return undefined;
          const filterObj = JSON.parse(filterStr);
          return filterObj.startDate
            ? new Date(filterObj.startDate)
            : undefined;
        } catch {
          return undefined;
        }
      })(),
      selectedOperator: (() => {
        try {
          const filterStr = appliedFilters["lastModifiedDate"]?.[0];
          if (!filterStr) return ">=";
          const filterObj = JSON.parse(filterStr);
          return filterObj.operator || ">=";
        } catch {
          return ">=";
        }
      })(),
      onChange: (date?: Date, operator?: string) => {
        setFilters((prev) => {
          if (!date || !operator) {
            const { lastModifiedDate: _removed, ...rest } = prev;
            return rest;
          }

          return {
            ...prev,
            lastModifiedDate: [
              JSON.stringify({
                startDate: date.toISOString(),
                operator,
                endDate: undefined, // Optional for future use
              }),
            ],
          };
        });
      },
    },
    {
      key: "duration",
      text: "Runtime",
      type: FilterTypes.Duration,
      clearable: true,
      selection: appliedFilters["duration"]?.[0] as unknown as DurationOption,
      increment: DurationIncrement.Day,
      onChange: (
        duration: number | undefined,
        increment: DurationIncrement,
        operator: Operator,
      ) => {
        setFilters((prev) => {
          if (!duration) {
            const { duration: _removed, ...rest } = prev;
            return rest;
          }

          return {
            ...prev,
            duration: [
              {
                duration,
                increment,
                operator,
              },
            ],
          };
        });
      },
    },
  ];
};

const customStatusFilter =
  (runPipelineStatuses: PipelineStatusMap) =>
  (items: ExperimentType[], selectedValues: string[]) => {
    if (selectedValues.length === 0) return items;

    return items.filter((item) => {
      const configFileName = item.__filename;
      const itemStatusMap = runPipelineStatuses[configFileName];
      const itemStatuses = [
        ...(itemStatusMap?.[RC_TYPE]?.statuses || []),
        ...(itemStatusMap?.[MPAC_TYPE]?.statuses || []),
        ...(itemStatusMap?.[PROD_TYPE]?.statuses || []),
      ].map(({ status }) => normalizeStatus(status));
      return itemStatuses.some((status) => selectedValues.includes(status));
    });
  };

const customLastModifiedDateFilter = (
  items: ExperimentType[],
  selectedValues: string[],
) => {
  if (!selectedValues || selectedValues.length === 0) return items;

  try {
    const filterStr = selectedValues[0];
    const filterObj = JSON.parse(filterStr);

    if (!filterObj || !filterObj.startDate) return items;

    const selectedDate = new Date(filterObj.startDate);
    const operator = (filterObj.operator as DateOperator) || ">=";

    return items.filter((item) => {
      if (!item.lastModifiedDate) return false;
      const itemDate = new Date(item.lastModifiedDate);
      switch (operator) {
        case ">":
          return itemDate > selectedDate;
        case ">=":
          return itemDate >= selectedDate;
        case "<":
          return itemDate < selectedDate;
        case "<=":
          return itemDate <= selectedDate;
        case "=":
          // Compare just the date part (not time)
          const selectedDateOnly = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
          );
          const itemDateOnly = new Date(
            itemDate.getFullYear(),
            itemDate.getMonth(),
            itemDate.getDate(),
          );
          return itemDateOnly.getTime() === selectedDateOnly.getTime();
        case "!=":
          // Compare just the date part (not time)
          const selectedDateOnlyNot = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
          );
          const itemDateOnlyNot = new Date(
            itemDate.getFullYear(),
            itemDate.getMonth(),
            itemDate.getDate(),
          );
          return itemDateOnlyNot.getTime() !== selectedDateOnlyNot.getTime();
        default:
          return true;
      }
    });
  } catch (error) {
    console.error("Error parsing lastModifiedDate filter:", error);
    return items;
  }
};

const customDurationFilter =
  (runPipelineStatuses: PipelineStatusMap) =>
  (items: ExperimentType[], selectedValues: string[]) => {
    if (!selectedValues || selectedValues.length === 0) return items;

    const filterObj = selectedValues[0] as unknown as DurationOption;
    const { duration, operator } = filterObj;
    let filtered = items.filter((item) => {
      const configFileName = item.__filename;
      const itemStatusMap = runPipelineStatuses[configFileName];
      const daysSinceInAllEnvs = [
        itemStatusMap?.[RC_TYPE]?.daysSinceStart,
        itemStatusMap?.[MPAC_TYPE]?.daysSinceStart,
        itemStatusMap?.[PROD_TYPE]?.daysSinceStart,
      ].filter((d): d is number => d !== undefined);

      switch (operator) {
        case ">":
          return daysSinceInAllEnvs.some((days) => days > duration);
        case ">=":
          return daysSinceInAllEnvs.some((days) => days >= duration);
        case "<":
          return daysSinceInAllEnvs.some((days) => days < duration);
        case "<=":
          return daysSinceInAllEnvs.some((days) => days <= duration);
        case "=":
          return daysSinceInAllEnvs.some((days) => days === duration);
        case "!=":
          return daysSinceInAllEnvs.some((days) => days !== duration);
        default:
          return true;
      }
    });

    return filtered;
  };

export {
  getFilters,
  customStatusFilter,
  customLastModifiedDateFilter,
  customDurationFilter,
};
